import { supabase } from './supabase.js';

/**
 * Clean, simple service for managing questionnaire responses
 * Designed for easy GPT prompt generation
 */
export class QuestionnaireService {
  
  /**
   * Generate a unique session ID
   * @returns {string} Unique session identifier
   */
  static generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  /**
   * Save questionnaire response to database
   * @param {Object} formData - Complete form data from the questionnaire
   * @param {string} sessionId - Optional session ID (will generate if not provided)
   * @returns {Promise<Object>} Saved response with ID and session ID
   */
  static async saveResponse(formData, sessionId = null) {
    try {
      // Generate session ID if not provided
      const responseSessionId = sessionId || this.generateSessionId();
      
      // Structure the data for easy GPT processing
      const responseData = {
        session_id: responseSessionId,
        
        // Step 1: Basic Information
        full_name: formData.fullName || null,
        company_name: formData.companyName || null,
        business_area: formData.businessArea || null,
        
                // Step 2: Project Details  
        project_types: formData.projectTypes || null, // This is already an array
        app_details: formData.appDetails || null,
        
        // AI Workflow Integration
        ai_question: formData.aiQuestion || null,
        user_response: formData.userResponse || null,
        
        // Step 3: Brand/Visual Identity
        brand_status: formData.brandStatus || null,
        style_preference: formData.stylePreference || null,
        design_reference: formData.designReference || null,
        
        // Step 4: Timeline
        timeline: formData.timeline || null,
        
        // Step 5: Contact Information
        phone: formData.phone || null,
        email: formData.email || null,
        additional_info: formData.additionalInfo || null,
        
        // Store the complete raw data for GPT processing
        raw_form_data: formData,
        status: 'completed'
      };

      const { data, error } = await supabase
        .from('questionnaire_responses')
        .insert(responseData)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to save questionnaire response: ${error.message}`);
      }

      console.log('✅ Questionnaire response saved successfully:', data.id);
      return data;
      
    } catch (error) {
      console.error('❌ Error saving questionnaire response:', error);
      throw error;
    }
  }

  /**
   * Get a response by session ID
   * @param {string} sessionId - Session identifier
   * @returns {Promise<Object>} Response data
   */
  static async getResponseBySessionId(sessionId) {
    try {
      const { data, error } = await supabase
        .from('questionnaire_responses')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (error) {
        throw new Error(`Failed to get response: ${error.message}`);
      }

      return data;
      
    } catch (error) {
      console.error('❌ Error getting response:', error);
      throw error;
    }
  }

  /**
   * Get responses for GPT processing (formatted for easy prompt generation)
   * @param {string} sessionId - Session identifier
   * @returns {Promise<Object>} Formatted data ready for GPT
   */
  static async getResponseForGPT(sessionId) {
    try {
      const response = await this.getResponseBySessionId(sessionId);
      
      // Format the data in a clean way for GPT processing
      const gptData = {
        basicInfo: {
          fullName: response.full_name,
          companyName: response.company_name,
          businessArea: response.business_area
        },
        projectDetails: {
          projectTypes: response.project_types,
          appDetails: response.app_details
        },
        aiWorkflow: {
          aiQuestion: response.ai_question,
          userResponse: response.user_response
        },
        brandIdentity: {
          brandStatus: response.brand_status,
          stylePreference: response.style_preference,
          designReference: response.design_reference
        },
        timeline: {
          timeline: response.timeline
        },
        contactInfo: {
          phone: response.phone,
          email: response.email,
          additionalInfo: response.additional_info
        },
        metadata: {
          sessionId: response.session_id,
          submittedAt: response.created_at,
          status: response.status
        }
      };

      return gptData;
      
    } catch (error) {
      console.error('❌ Error formatting response for GPT:', error);
      throw error;
    }
  }

  /**
   * Update response status (useful for tracking GPT processing)
   * @param {string} sessionId - Session identifier
   * @param {string} status - New status ('in_progress', 'completed', 'processed')
   */
  static async updateStatus(sessionId, status) {
    try {
      const { error } = await supabase
        .from('questionnaire_responses')
        .update({ status })
        .eq('session_id', sessionId);

      if (error) {
        throw new Error(`Failed to update status: ${error.message}`);
      }

      console.log(`✅ Status updated to ${status} for session ${sessionId}`);
      
    } catch (error) {
      console.error('❌ Error updating status:', error);
      throw error;
    }
  }

  /**
   * Get all recent responses (useful for admin/review purposes)
   * @param {number} limit - Number of responses to retrieve (default: 50)
   * @returns {Promise<Array>} Array of recent responses
   */
  static async getRecentResponses(limit = 50) {
    try {
      const { data, error } = await supabase
        .from('questionnaire_responses')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw new Error(`Failed to get recent responses: ${error.message}`);
      }

      return data;
      
    } catch (error) {
      console.error('❌ Error getting recent responses:', error);
      throw error;
    }
  }

  /**
   * Format response data for a GPT prompt (as a single string)
   * @param {string} sessionId - Session identifier
   * @returns {Promise<string>} Formatted string ready for GPT prompt
   */
  static async formatForGPTPrompt(sessionId) {
    try {
      const data = await this.getResponseForGPT(sessionId);
      
      let prompt = `Here is the questionnaire response data:\n\n`;
      prompt += `**Client Information:**\n`;
      prompt += `- Name: ${data.basicInfo.fullName}\n`;
      prompt += `- Company: ${data.basicInfo.companyName}\n`;
      prompt += `- Business Area: ${data.basicInfo.businessArea}\n\n`;
      
      prompt += `**Project Details:**\n`;
      prompt += `- Project Types: ${data.projectDetails.projectTypes ? data.projectDetails.projectTypes.join(', ') : 'Not specified'}\n`;
      prompt += `- Project Details: ${data.projectDetails.appDetails || 'Not specified'}\n\n`;
      
      prompt += `**AI Follow-up:**\n`;
      prompt += `- AI Question: ${data.aiWorkflow.aiQuestion || 'Not generated yet'}\n`;
      prompt += `- User Response: ${data.aiWorkflow.userResponse || 'Not answered yet'}\n\n`;
      
      prompt += `**Brand & Design:**\n`;
      prompt += `- Brand Status: ${data.brandIdentity.brandStatus || 'Not specified'}\n`;
      prompt += `- Style Preference: ${data.brandIdentity.stylePreference || 'Not specified'}\n`;
      prompt += `- Design Reference: ${data.brandIdentity.designReference || 'Not specified'}\n\n`;
      
      prompt += `**Timeline:**\n`;
      prompt += `- Timeline: ${data.timeline.timeline || 'Not specified'}\n\n`;
      
      prompt += `**Contact & Additional Info:**\n`;
      prompt += `- Phone: ${data.contactInfo.phone}\n`;
      prompt += `- Email: ${data.contactInfo.email}\n`;
      prompt += `- Additional Info: ${data.contactInfo.additionalInfo || 'None provided'}\n\n`;
      
      return prompt;
      
    } catch (error) {
      console.error('❌ Error formatting for GPT prompt:', error);
      throw error;
    }
  }

  /**
   * Save questionnaire response (draft or final)
   * Pulls session_id from localStorage, inserts if no row exists, updates if exists
   * @param {Object} payload - Form data to save (can include status: 'in_progress' or 'completed')
        * @returns {Promise<string>} The row ID, also cached in localStorage as nukard_record_id
   */
  static async saveDraft(payload) {
    try {
      // Get session_id from localStorage
      let sessionId = localStorage.getItem('session_id');
      
      // Generate session_id if it doesn't exist
      if (!sessionId) {
        sessionId = this.generateSessionId();
        localStorage.setItem('session_id', sessionId);
      }

      // Check if a row already exists for this session_id
      const { data: existingData } = await supabase
        .from('questionnaire_responses')
        .select('id')
        .eq('session_id', sessionId)
        .single();

      let result;

      if (existingData) {
        // Row exists, update it
        const { data, error } = await supabase
          .from('questionnaire_responses')
          .update({
            ...payload,
            raw_form_data: payload.raw_form_data || payload,
            updated_at: new Date().toISOString()
          })
          .eq('session_id', sessionId)
          .select('id')
          .single();

        if (error) {
          throw new Error(`Failed to update draft: ${error.message}`);
        }

        result = data;
        console.log('✅ Record updated successfully:', data.id, 'Status:', payload.status || 'in_progress');
      } else {
        // No row exists, insert new one
        const { data, error } = await supabase
          .from('questionnaire_responses')
          .insert({
            session_id: sessionId,
            ...payload,
            raw_form_data: payload.raw_form_data || payload,
            status: payload.status || 'in_progress' // Respeita o status do payload
          })
          .select('id')
          .single();

        if (error) {
          throw new Error(`Failed to insert draft: ${error.message}`);
        }

        result = data;
        console.log('✅ Record inserted successfully:', data.id, 'Status:', payload.status || 'in_progress');
      }

      // Cache the row ID in localStorage
      localStorage.setItem('nukard_record_id', result.id);

      return result.id;
      
    } catch (error) {
      // Handle the case where no row is found (not an error for our use case)
      if (error.message && error.message.includes('No rows returned')) {
        // No existing row, proceed with insert
        try {
          const sessionId = localStorage.getItem('session_id') || this.generateSessionId();
          if (!localStorage.getItem('session_id')) {
            localStorage.setItem('session_id', sessionId);
          }

          const { data, error } = await supabase
            .from('questionnaire_responses')
            .insert({
              session_id: sessionId,
              ...payload,
              raw_form_data: payload.raw_form_data || payload,
              status: payload.status || 'in_progress' // Respeita o status do payload
            })
            .select('id')
            .single();

          if (error) {
            throw new Error(`Failed to insert draft: ${error.message}`);
          }

          localStorage.setItem('nukard_record_id', data.id);
          console.log('✅ Record inserted successfully:', data.id);
          return data.id;
        } catch (insertError) {
          console.error('❌ Error inserting draft:', insertError);
          throw insertError;
        }
      } else {
        console.error('❌ Error saving draft:', error);
        throw error;
      }
    }
  }

  /**
   * Update AI question for a specific session
   * @param {string} sessionId - Session identifier
   * @param {string} aiQuestion - AI generated question
   */
  static async updateAIQuestion(sessionId, aiQuestion) {
    try {
      const { error } = await supabase
        .from('questionnaire_responses')
        .update({ 
          ai_question: aiQuestion,
          updated_at: new Date().toISOString()
        })
        .eq('session_id', sessionId);

      if (error) {
        throw new Error(`Failed to update AI question: ${error.message}`);
      }

      console.log(`✅ AI question updated for session ${sessionId}`);
      
    } catch (error) {
      console.error('❌ Error updating AI question:', error);
      throw error;
    }
  }

  /**
   * Save user response to AI question
   * @param {string} sessionId - Session identifier  
   * @param {string} userResponse - User's response to AI question
   */
  static async saveUserResponse(sessionId, userResponse) {
    try {
      const { error } = await supabase
        .from('questionnaire_responses')
        .update({ 
          user_response: userResponse,
          updated_at: new Date().toISOString()
        })
        .eq('session_id', sessionId);

      if (error) {
        throw new Error(`Failed to save user response: ${error.message}`);
      }

      console.log(`✅ User response saved for session ${sessionId}`);
      
    } catch (error) {
      console.error('❌ Error saving user response:', error);
      throw error;
    }
  }

  /**
   * Get AI question for a specific session
   * @param {string} sessionId - Session identifier
   * @returns {Promise<Object>} AI question and user response data
   */
  static async getAIWorkflow(sessionId) {
    try {
      const { data, error } = await supabase
        .from('questionnaire_responses')
        .select('ai_question, user_response, status')
        .eq('session_id', sessionId)
        .single();

      if (error) {
        throw new Error(`Failed to get AI workflow: ${error.message}`);
      }

      return {
        aiQuestion: data.ai_question,
        userResponse: data.user_response,
        status: data.status
      };
      
    } catch (error) {
      console.error('❌ Error getting AI workflow:', error);
      throw error;
    }
  }
} 